import { useEffect, useState, useCallback } from "react";
import { useNavigate, useSearchParams } from "react-router-dom";
import { Plus, FileText, Trash2 } from "lucide-react";
import { toast } from "sonner";
import { api, apiError } from "@/lib/api";
import { useCompany } from "@/context/CompanyContext";
import { fmtEur, fmtDate, MOIS, STATUS_LABELS, STATUS_CLASSES, TYPE_LABELS } from "@/lib/format";
import { Button } from "@/components/ui/button";
import { Label } from "@/components/ui/label";
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from "@/components/ui/table";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter, DialogDescription } from "@/components/ui/dialog";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";

export default function DeclarationsPage() {
  const { company, companyId, year } = useCompany();
  const [list, setList] = useState([]);
  const [open, setOpen] = useState(false);
  const [form, setForm] = useState({ type: "annuelle", periode: 1 });
  const [busy, setBusy] = useState(false);
  const navigate = useNavigate();
  const [sp] = useSearchParams();
  const filter = sp.get("type");
  const FILTER_LABELS = { acomptes: "Acomptes 2501-SD", annuelle: "Liquidation annuelle 2502-SD" };
  const visible = list.filter((d) => !filter || (filter === "annuelle" ? d.type === "annuelle" : d.type !== "annuelle"));

  const load = useCallback(() => {
    if (!companyId) return;
    api.get(`/companies/${companyId}/declarations`, { params: { annee: year } }).then((r) => setList(r.data)).catch((e) => toast.error(apiError(e)));
  }, [companyId, year]);
  useEffect(load, [load]);

  const create = async () => {
    setBusy(true);
    try {
      const { data } = await api.post(`/companies/${companyId}/declarations`, { type: form.type, annee: year, periode: form.type === "annuelle" ? 0 : form.periode });
      toast.success(`Déclaration ${data.label} générée`);
      setOpen(false);
      navigate(`/declarations/${data.id}`);
    } catch (e) { toast.error(apiError(e)); } finally { setBusy(false); }
  };

  const remove = async (d) => {
    if (!window.confirm(`Supprimer la déclaration ${d.label} ?`)) return;
    try { await api.delete(`/declarations/${d.id}`); toast.success("Déclaration supprimée"); load(); } catch (e) { toast.error(apiError(e)); }
  };

  if (!company) return <div className="text-muted-foreground">Créez d'abord une entreprise dans Paramètres.</div>;
  const periods = form.type === "mensuelle" ? MOIS.map((m, i) => [i + 1, m]) : form.type === "trimestrielle" ? [[1, "T1"], [2, "T2"], [3, "T3"], [4, "T4"]] : [];

  return (
    <div className="space-y-6" data-testid="declarations-page">
      <div className="flex items-end justify-between">
        <div>
          <div className="eyebrow">{company.name} · exercice {year}</div>
          <h1 className="font-heading text-3xl font-extrabold tracking-tight text-foreground mt-1">Déclarations{filter && <span className="text-brand-500" data-testid="declarations-filter-label"> · {FILTER_LABELS[filter]}</span>}</h1>
        </div>
        <Button onClick={() => setOpen(true)} data-testid="declaration-create-button"><Plus className="h-4 w-4 mr-2" /> Nouvelle déclaration</Button>
      </div>

      <div className="bg-card border border-border rounded-md overflow-hidden">
        <Table data-testid="declarations-table">
          <TableHeader>
            <TableRow className="bg-muted/60">
              <TableHead>Période</TableHead>
              <TableHead>Formulaire</TableHead>
              <TableHead>Échéance</TableHead>
              <TableHead className="text-right">Masse salariale</TableHead>
              <TableHead className="text-right">Taxe nette</TableHead>
              <TableHead>Statut</TableHead>
              <TableHead className="w-16" />
            </TableRow>
          </TableHeader>
          <TableBody>
            {visible.length === 0 && (
              <TableRow><TableCell colSpan={7} className="text-center py-12 text-muted-foreground" data-testid="declarations-empty"><FileText className="h-8 w-8 mx-auto text-muted-foreground/50 mb-2" />Aucune déclaration pour {year}.</TableCell></TableRow>
            )}
            {visible.map((d) => (
              <TableRow key={d.id} className="cursor-pointer hover:bg-muted/50" onClick={() => navigate(`/declarations/${d.id}`)} data-testid="declaration-row">
                <TableCell className="font-medium text-foreground">{d.label}</TableCell>
                <TableCell className="font-mono text-xs text-muted-foreground">{d.type === "annuelle" ? "2502-SD" : "2501-SD"} · {TYPE_LABELS[d.type].split(" ")[0]}</TableCell>
                <TableCell className="font-mono text-foreground/80">{fmtDate(d.echeance)}</TableCell>
                <TableCell className="text-right font-mono">{fmtEur(d.result.masse_salariale)}</TableCell>
                <TableCell className="text-right font-mono font-semibold" data-testid="declaration-net">{fmtEur(d.result.taxe_nette)}</TableCell>
                <TableCell><span className={`text-[11px] px-2 py-0.5 rounded border ${STATUS_CLASSES[d.status]}`} data-testid="declaration-status-badge">{STATUS_LABELS[d.status]}</span></TableCell>
                <TableCell><Button size="icon" variant="ghost" className="text-red-600 dark:text-red-400" onClick={(e) => { e.stopPropagation(); remove(d); }} data-testid="declaration-delete-button"><Trash2 className="h-4 w-4" /></Button></TableCell>
              </TableRow>
            ))}
          </TableBody>
        </Table>
      </div>

      <Dialog open={open} onOpenChange={setOpen}>
        <DialogContent data-testid="declaration-create-dialog">
          <DialogHeader>
            <DialogTitle className="font-heading">Nouvelle déclaration · {year}</DialogTitle>
            <DialogDescription>La déclaration est calculée à partir des rémunérations saisies pour la période. Acomptes 2501-SD (mensuel / trimestriel) ou liquidation annuelle 2502-SD.</DialogDescription>
          </DialogHeader>
          <div className="grid grid-cols-2 gap-3">
            <div>
              <Label>Type</Label>
              <Select value={form.type} onValueChange={(v) => setForm({ type: v, periode: 1 })}>
                <SelectTrigger data-testid="declaration-type-select"><SelectValue /></SelectTrigger>
                <SelectContent>{Object.entries(TYPE_LABELS).map(([k, v]) => <SelectItem key={k} value={k} data-testid={`declaration-type-${k}`}>{v}</SelectItem>)}</SelectContent>
              </Select>
            </div>
            {periods.length > 0 && (
              <div>
                <Label>Période</Label>
                <Select value={String(form.periode)} onValueChange={(v) => setForm({ ...form, periode: Number(v) })}>
                  <SelectTrigger data-testid="declaration-period-select"><SelectValue /></SelectTrigger>
                  <SelectContent>{periods.map(([v, l]) => <SelectItem key={v} value={String(v)}>{l}</SelectItem>)}</SelectContent>
                </Select>
              </div>
            )}
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setOpen(false)}>Annuler</Button>
            <Button onClick={create} disabled={busy} data-testid="declaration-create-submit">{busy ? "Calcul…" : "Générer"}</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}
